import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { Clipboard } from '@angular/cdk/clipboard';
import { computed, inject, Signal } from '@angular/core';
import { Router } from '@angular/router';
import { NewsArticlesStore } from '@appState';
import { findAndRemove } from '@appState/helpers/helper';
import { createCustomStateSetOnRegexHandler, createCustomStateSetOnUrlHandler, guidRegex } from '@appState/helpers/state-reset-handler';
import { getAll, getOne, StateDictionary, updateEntity } from '@appState/helpers/store-base';
import { NewsArticleGroup, UpdateNewsArticleGroupRequest } from '@models/news-article-group/news-article-group.model';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationsService } from 'cui-components';
import { catchError, EMPTY, pipe, switchMap, tap } from 'rxjs';
import { AppConfigService } from 'src/app/infrastructure/app-config.service';
import { NewsArticleGroupsService } from './news-article-groups.service';

interface NewsArticleGroupsState {
  groups: NewsArticleGroup[];
  isLoading: boolean;
  selectedGroup: NewsArticleGroup | null;
  isSelectedGroupLoading: boolean;
  isFieldExpertsLoading: boolean;
}

const initialState: NewsArticleGroupsState = {
  groups: [],
  isLoading: false,
  selectedGroup: null,
  isSelectedGroupLoading: false,
  isFieldExpertsLoading: false,
};

const groupsDictionary: StateDictionary<NewsArticleGroup> = {
  entityList: 'groups',
  listLoading: 'isLoading',
  selectedEntityLoading: 'isSelectedGroupLoading',
  selectedEntity: 'selectedGroup',
  entityId: 'id',
  createLoading: '',
};

export const NewsArticleGroupsStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withDevtools('news-article-groups-store'),
  withComputed(state => ({
    selectedGroupId: computed(() => state.selectedGroup()?.id ?? null),
  })),
  withMethods(
    (
      store,
      groupsService = inject(NewsArticleGroupsService),
      router = inject(Router),
      clipboard = inject(Clipboard),
      notifications = inject(NotificationsService),
      appConfig = inject(AppConfigService),
      newsArticlesStore = inject(NewsArticlesStore),
    ) => {
      createCustomStateSetOnUrlHandler<NewsArticleGroupsState>('/marketing/news-articles', {
        selectedGroup: null,
      })(store);
      createCustomStateSetOnRegexHandler<NewsArticleGroupsState>(new RegExp(`^/marketing/communities/${guidRegex}/news-articles$`))(store, {
        selectedGroup: null,
      });

      const repositoryMethods = {
        getAll: getAll<NewsArticleGroup>({
          store,
          service: groupsService,
          dictionary: groupsDictionary,
        }),

        getOne: getOne<NewsArticleGroup>({
          store,
          service: groupsService,
          dictionary: groupsDictionary,
        }),

        update: updateEntity<NewsArticleGroup, UpdateNewsArticleGroupRequest>({
          store,
          service: groupsService,
          dictionary: groupsDictionary,
          onSuccessCallback: updatedGroup => newsArticlesStore.modifyGroupInfoForArticlesInGroup(updatedGroup),
        }),

        setMembershipLevels: rxMethod<{ id: string; membershipLevels: string[] }>(
          pipe(
            tap(() => patchState(store, { isSelectedGroupLoading: true })),
            switchMap(({ id, membershipLevels }) =>
              groupsService.setMembershipLevels(id, membershipLevels).pipe(
                tap(group => {
                  patchState(store, {
                    selectedGroup: group,
                    isSelectedGroupLoading: false,
                  });
                  newsArticlesStore.updateMembershipLevelsForGroupArticles(group);
                }),
                catchError(() => {
                  patchState(store, { isSelectedGroupLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        setWorkloads: rxMethod<{ id: string; workloads: string[] }>(
          pipe(
            tap(() => patchState(store, { isSelectedGroupLoading: true })),
            switchMap(({ id, workloads }) =>
              groupsService.setWorkloads(id, workloads).pipe(
                tap(group => {
                  patchState(store, {
                    selectedGroup: group,
                    isSelectedGroupLoading: false,
                  });
                  newsArticlesStore.updateWorkloadsForGroupArticles(group);
                }),
                catchError(() => {
                  patchState(store, { isSelectedGroupLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        addFieldExpert: rxMethod<{ groupId: string; fieldExpertId: string }>(
          pipe(
            tap(() => patchState(store, { isFieldExpertsLoading: true })),
            switchMap(({ groupId, fieldExpertId }) =>
              groupsService.addFieldExpert(groupId, fieldExpertId).pipe(
                tap(group => {
                  patchState(store, {
                    selectedGroup: group,
                    isFieldExpertsLoading: false,
                  });
                  newsArticlesStore.addFieldExpertToGroupArticles(group, fieldExpertId);
                }),
                catchError(() => {
                  patchState(store, { isFieldExpertsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),

        removeFieldExpert: rxMethod<{ groupId: string; fieldExpertId: string }>(
          pipe(
            tap(() => patchState(store, { isFieldExpertsLoading: true })),
            switchMap(({ groupId, fieldExpertId }) =>
              groupsService.deleteFieldExpert(groupId, fieldExpertId).pipe(
                tap(() => {
                  const selectedGroup = store.selectedGroup()!;
                  patchState(store, {
                    selectedGroup: {
                      ...selectedGroup,
                      fieldExperts: findAndRemove(selectedGroup.fieldExperts, entity => entity.id === fieldExpertId),
                    },
                    isFieldExpertsLoading: false,
                  });
                  newsArticlesStore.removeFieldExpertFromGroupArticles(selectedGroup, fieldExpertId);
                }),
                catchError(() => {
                  patchState(store, { isFieldExpertsLoading: false });
                  return EMPTY;
                }),
              ),
            ),
          ),
        ),
      };

      const utilMethods = {
        copyExternalLink: (groupId: string): void => {
          clipboard.copy(`${appConfig.config['FRONTEND_PORTAL_URL']}/news-articles/${groupId}`);
          notifications.showNotification('Copied successfully!');
        },
      };

      const navigationActions = {
        goToActions: async (id: string, communityId?: string): Promise<boolean> => {
          const result = communityId
            ? await router.navigate(['/marketing', 'communities', communityId, 'news-articles', 'group', id])
            : await router.navigate(['/marketing', 'news-articles', 'group', id]);
          if (!result) return false;

          if (!store.isSelectedGroupLoading()) repositoryMethods.getOne(id);

          return result;
        },

        resetState: (state: Partial<NewsArticleGroupsState>): void => {
          patchState(store, {
            ...state,
          });
        },
      };

      return {
        ...repositoryMethods,
        ...utilMethods,
        ...navigationActions,
      };
    },
  ),
);

export interface NewsArticleGroupsStoreType {
  readonly groups: Signal<NewsArticleGroup[]>;
  readonly isLoading: Signal<boolean>;
  readonly selectedGroup: Signal<NewsArticleGroup | null>;
  readonly isSelectedGroupLoading: Signal<boolean>;
  readonly selectedGroupId: Signal<string | null>;
  readonly isFieldExpertsLoading: Signal<boolean>;

  readonly getAll: () => void;
  readonly getOne: (id: string) => void;
  readonly update: (params: { id: string; dto: UpdateNewsArticleGroupRequest }) => void;
  readonly setMembershipLevels: (params: { id: string; membershipLevels: string[] }) => void;
  readonly setWorkloads: (params: { id: string; workloads: string[] }) => void;
  readonly addFieldExpert: (params: { groupId: string; fieldExpertId: string }) => void;
  readonly removeFieldExpert: (params: { groupId: string; fieldExpertId: string }) => void;

  readonly copyExternalLink: (groupId: string) => void;

  readonly goToActions: (id: string, communityId?: string) => Promise<boolean>;
  readonly resetState: (state: Partial<NewsArticleGroupsState>) => void;
}
